import type { ReactNode } from "react";
import type { CommunicationInboxItem } from "@/types/communications";
import type { ViewMode } from "@/hooks/use-view-preferences";
import { CommunicationMessageRow } from "@/components/communications/CommunicationMessageRow";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface CommunicationMessageListProps {
  items: CommunicationInboxItem[];
  viewMode: ViewMode;
  selectedIds: Record<string, true>;
  onToggleSelect: (id: string) => void;
  onSelectItem: (id: string) => void;
  activeId?: string | null;
  disabled?: boolean;
  emptyMessage?: string;
  renderTrailingBadge?: (item: CommunicationInboxItem) => ReactNode;
  allSelected?: boolean;
  someSelected?: boolean;
  onSelectAllChange?: (checked: boolean) => void;
}

function formatDate(date: string): string {
  try {
    return new Date(date).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return date;
  }
}

export function CommunicationMessageList({
  items,
  viewMode,
  selectedIds,
  onToggleSelect,
  onSelectItem,
  activeId,
  disabled = false,
  emptyMessage = "No messages found",
  renderTrailingBadge,
  allSelected = false,
  someSelected = false,
  onSelectAllChange,
}: CommunicationMessageListProps) {
  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground text-center py-8">{emptyMessage}</p>;
  }

  if (viewMode === "grid") {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((item) => (
          <Card
            key={item.id}
            className={cn(
              "cursor-pointer hover:border-primary/40 transition-colors",
              activeId === item.id && "border-primary/40 bg-primary/5"
            )}
            onClick={() => !disabled && onSelectItem(item.id)}
          >
            <CardHeader className="p-3 pb-1">
              <div className="flex items-start gap-2">
                <Checkbox
                  checked={!!selectedIds[item.id]}
                  disabled={disabled}
                  onCheckedChange={() => onToggleSelect(item.id)}
                  onClick={(e) => e.stopPropagation()}
                />
                <CardTitle className="text-sm font-medium line-clamp-2">{item.subject}</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-3 pt-1 space-y-1">
              <p className="text-xs text-muted-foreground truncate">{item.from}</p>
              <p className="text-xs line-clamp-2">{item.preview}</p>
              <div className="flex items-center justify-between pt-1">
                <span className="text-[10px] text-muted-foreground">{formatDate(item.date)}</span>
                {item.type && <Badge variant="outline" className="text-[10px] capitalize">{item.type}</Badge>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (viewMode === "table") {
    return (
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8">
                {onSelectAllChange && (
                  <Checkbox
                    checked={allSelected ? true : someSelected ? "indeterminate" : false}
                    disabled={disabled || items.length === 0}
                    onCheckedChange={(checked) => onSelectAllChange(checked === true)}
                    aria-label="Select all on page"
                  />
                )}
              </TableHead>
              <TableHead>Subject</TableHead>
              <TableHead>From</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Type</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <TableRow
                key={item.id}
                className={cn("cursor-pointer", activeId === item.id && "bg-primary/5")}
                onClick={() => !disabled && onSelectItem(item.id)}
              >
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={!!selectedIds[item.id]}
                    disabled={disabled}
                    onCheckedChange={() => onToggleSelect(item.id)}
                  />
                </TableCell>
                <TableCell className={cn("font-medium max-w-[200px] truncate", !item.read && "font-semibold")}>
                  {item.subject}
                </TableCell>
                <TableCell className="max-w-[150px] truncate text-muted-foreground">{item.from}</TableCell>
                <TableCell className="text-muted-foreground whitespace-nowrap">{formatDate(item.date)}</TableCell>
                <TableCell>
                  {item.type && <Badge variant="outline" className="text-[10px] capitalize">{item.type}</Badge>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {items.map((item) => (
        <CommunicationMessageRow
          key={item.id}
          item={item}
          selected={!!selectedIds[item.id]}
          onSelect={onToggleSelect}
          onClick={onSelectItem}
          isActive={activeId === item.id}
          disabled={disabled}
          trailingBadge={renderTrailingBadge?.(item)}
        />
      ))}
    </div>
  );
}
